
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { StorageService } from "../services/storage.service";

/**
 * Entry points that open the app from outside it: custom-scheme URLs (vnctask:),
 * universal links, and the share sheet. Each one ends in either a router navigation or a
 * window.postMessage that the shell's window-message handler picks up. Extracted from
 * AppComponent (STR-05).
 */
@Injectable({
  providedIn: "root"
})
export class DeepLinkService {
  private router = inject(Router);

  /** Installs the global handleOpenURL hook Cordova calls for vnctask: URLs. */
  public setupHandleOpenURL(): void {
    console.log("[DeepLinkService] setupHandleOpenURL");
    window.handleOpenURL = url => {
      console.log("[DeepLinkService][handleOpenURL]", url, typeof url);
      if (url.startsWith("vnctask:")) {
        console.log("[DeepLinkService][vnctask] Actions");

        if (url.includes("action=compose")) {
          const mailAddress = url.split("assignee=")[1];
          setTimeout(() => {
            console.log("[DeepLinkService][createtask] ", mailAddress);
            const timestamp = new Date().getTime();
            this.router.navigate(["/task/open"], { queryParams: { action: "compose", assignee: mailAddress, description: "", timestamp: timestamp } });
          }, 1000);
        }
      }
    };
  }

  /** universalLinks "launchedAppFromLink" handler. */
  public onApplicationDidLaunchFromLink(eventData): void {
    const params = eventData?.params;
    if (!params) {
      return;
    }

    switch (params.action) {
      case "confirm-email":
        if (params.token) {
          StorageService.setLocalItem("activation_token", params.token);
          window.postMessage({ type: "GO_TO_CONFIRM_EMAIL" }, "*");
        }
        break;
      case "password-reset":
        if (params.token) {
          StorageService.setLocalItem("password_reset_token", params.token);
          window.postMessage({ type: "GO_TO_RESET_PASSWORD_PAGE" }, "*");
        }
        break;
      case "open-task":
        if (params.id) {
          window.postMessage({ type: "OPEN_TASK_DETAIL_PAGE", id: params.id }, "*");
        }
        break;
      case "task-invitation":
        if (params.token) {
          StorageService.setLocalItem("invitation_token", params.token);
          window.postMessage({ type: "GO_TO_TASK_INVITATION" }, "*");
        }
        break;
      case "show-diff":
        if (params.journal_id && params.detail_id && params.id) {
          window.postMessage({
            type: "SHOW_DESCRIPTION_DIFFERENCE",
            id: params.id,
            journal_id: params.journal_id,
            detail_id: params.detail_id
          }, "*");
        }
        break;
    }
  }

  /** universalLinks "triggerActionEvent" handler. */
  public processTriggerActionEvent(eventData): void {
    console.log("[DeepLinkService][processTriggerActionEvent]", eventData);
    const params = eventData?.params;
    if (params && eventData.path === "/task/open") {
      this.router.navigate([eventData.path], { queryParams: params });
    }
  }

  /** Opens the compose screen for content handed over by the OS share sheet. */
  public handleSharing(): void {
    const timestamp = new Date().getTime();
    this.router.navigate(["/task/open"], { queryParams: { action: "compose", description: "", timestamp: timestamp } });
  }
}
